// src/api/adminApi.js
import api from "./api";

/**
 * Admin API service
 * Provides methods for admin-specific operations
 */

// ==================== USER MANAGEMENT ====================

/**
 * Get all users (superadmin only)
 * @returns {Promise} List of all users
 */
export const getAllUsers = async () => {
  const response = await api.get("/admin/users");
  return response.data.data;
};

/**
 * Get users with statistics (admin/superadmin)
 * @returns {Promise} List of users with their statistics
 */
export const getUsersWithStatistics = async () => {
  const response = await api.get("/admin/users/statistics");
  return response.data.data;
};

/**
 * Get detailed statistics for a specific user
 * @param {string} userId - User ID
 * @returns {Promise} User's detailed statistics
 */
export const getUserStatistics = async (userId) => {
  const response = await api.get(`/admin/users/${userId}/statistics`);
  return response.data.data;
};

/**
 * Get user details (superadmin only)
 * @param {string} userId - User ID
 * @returns {Promise} User details
 */
export const getUserDetail = async (userId) => {
  const response = await api.get(`/admin/users/${userId}`);
  return response.data.data;
};

/**
 * Get all tasks for a specific user (admin/superadmin)
 * @param {string} userId - User ID
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number (default: 1)
 * @param {number} params.page_size - Items per page (default: 20, max: 100)
 * @param {string} params.status - Filter by status (optional)
 * @param {string} params.task_type - Filter by type: 'created', 'assigned', or null for both (optional)
 * @returns {Promise} Paginated tasks list
 */
export const getUserTasks = async (userId, params = {}) => {
  const response = await api.get(`/admin/users/${userId}/tasks`, { params });
  return response.data.data;
};

// ==================== ROLE MANAGEMENT ====================

/**
 * Get all roles (superadmin only)
 * @returns {Promise} List of all roles
 */
export const getAllRoles = async () => {
  const response = await api.get("/admin/roles");
  return response.data.data;
};

/**
 * Create a new role (superadmin only)
 * @param {Object} roleData - Role data
 * @param {string} roleData.code - Role code
 * @param {string} roleData.name - Role name
 * @param {number} roleData.z_index - Role z-index (hierarchy)
 * @returns {Promise} Created role
 */
export const createRole = async (roleData) => {
  const response = await api.post("/admin/role/create", roleData);
  return response.data.data;
};

/**
 * Update a role (superadmin only)
 * @param {string} roleId - Role ID
 * @param {Object} updateData - Updated role data
 * @param {number} updateData.z_index - New z-index
 * @returns {Promise} Updated role
 */
export const updateRole = async (roleId, updateData) => {
  const response = await api.put(`/admin/role/${roleId}`, updateData);
  return response.data.data;
};

/**
 * Assign role to user (superadmin only)
 * @param {string} userId - User ID
 * @param {string} roleId - Role ID
 * @returns {Promise} Success message
 */
export const assignRoleToUser = async (userId, roleId) => {
  const response = await api.post(`/admin/user/${userId}/roles/${roleId}`);
  return response.data.data;
};

/**
 * Remove role from user (superadmin only)
 * @param {string} userId - User ID
 * @param {string} roleId - Role ID
 * @returns {Promise} Success message
 */
export const removeRoleFromUser = async (userId, roleId) => {
  const response = await api.delete(`/admin/user/${userId}/roles/${roleId}`);
  return response.data.data;
};

/**
 * Get users by role (superadmin only)
 * @param {string} roleId - Role ID
 * @returns {Promise} List of users with this role
 */
export const getUsersByRole = async (roleId) => {
  const response = await api.get(`/admin/roles/${roleId}/users`);
  return response.data.data;
};

export default {
  getAllUsers,
  getUsersWithStatistics,
  getUserStatistics,
  getUserDetail,
  getUserTasks,
  getAllRoles,
  createRole,
  updateRole,
  assignRoleToUser,
  removeRoleFromUser,
  getUsersByRole,
};
